import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _excluded = ["calculateCheckboxBackgroundColor"],
    _excluded2 = ["calculateBorderColor"],
    _excluded3 = ["calculateBorderWidth"],
    _excluded4 = ["calculateBoxShadow"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { calculateRem } from '@vds-core/utilities';
import { ColorTokens } from '@vds-tokens/color';
import { FormControlsTokens } from '@vds-tokens/form-controls';
import { AccessibilityTokens } from '@vds-tokens/accessibility'; // Use checkmark-bold.svg

var CheckmarkBoldIcon = /*#__PURE__*/React.createElement("svg", {
  version: "1.1",
  id: "Layer_1",
  xmlns: "http://www.w3.org/2000/svg",
  x: "0px",
  y: "0px",
  viewBox: "0 0 21.6 21.6"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8,19.1l-7-7l2.5-2.5L8,14L18.1,3.8l2.5,2.5L8,19.1z"
}));

var _calculateBorderColor = function _calculateBorderColor(_ref) {
  var disabled = _ref.disabled,
      error = _ref.error,
      surface = _ref.surface,
      hovered = _ref.hovered,
      selected = _ref.selected;

  if (disabled) {
    return surface === 'dark' ? ColorTokens.interactive.disabled.ondark.value : ColorTokens.interactive.disabled.onlight.value;
  }

  if (error) {
    return surface === 'dark' ? ColorTokens.feedback.error.ondark.value : ColorTokens.feedback.error.onlight.value;
  }

  if (hovered && !selected) {
    return surface === 'dark' ? ColorTokens.elements.secondary.ondark.value : ColorTokens.elements.secondary.onlight.value;
  }

  return surface === 'dark' ? FormControlsTokens.color.border.ondark.value : FormControlsTokens.color.border.onlight.value;
};

var _calculateAdditionalBorderColor = function _calculateAdditionalBorderColor(surface, error, selected) {
  if (error) {
    return surface === 'dark' ? ColorTokens.feedback.error.ondark.value : ColorTokens.feedback.error.onlight.value;
  }

  if (selected) {
    return surface === 'dark' ? FormControlsTokens.color.border.hover.ondark.value : FormControlsTokens.color.border.hover.onlight.value;
  }

  return surface === 'dark' ? ColorTokens.elements.secondary.ondark.value : ColorTokens.elements.secondary.onlight.value;
};

var _calculateCheckboxBackgroundColor = function _calculateCheckboxBackgroundColor(_ref2) {
  var disabled = _ref2.disabled,
      selected = _ref2.selected,
      surface = _ref2.surface,
      error = _ref2.error;

  if (disabled && selected) {
    return surface === 'dark' ? ColorTokens.interactive.disabled.ondark.value : ColorTokens.interactive.disabled.onlight.value;
  }

  if (selected) {
    return surface === 'dark' ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value;
  }

  if (error && !disabled) {
    return surface === 'dark' ? ColorTokens.feedback.error.background.ondark.value : ColorTokens.feedback.error.background.onlight.value;
  }

  return 'transparent';
};

var _calculateBorderWidth = function _calculateBorderWidth(_ref3) {
  var selected = _ref3.selected;
  return selected ? 0 : calculateRem(AccessibilityTokens.focusring.borderwidth.value);
};

var _calculateBoxShadow = function _calculateBoxShadow(_ref4) {
  var hovered = _ref4.hovered,
      disabled = _ref4.disabled,
      surface = _ref4.surface,
      error = _ref4.error,
      selected = _ref4.selected;
  return hovered && !disabled ? " 0 0 0 ".concat(calculateRem(FormControlsTokens.border.width.value), " ").concat(_calculateAdditionalBorderColor(surface, error, selected)) : "none";
};

var propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  height: PropTypes.number,
  selected: PropTypes.bool,
  hovered: PropTypes.bool,
  width: PropTypes.number,

  /**
   * @ignore
   */
  surface: PropTypes.oneOf(['light', 'dark']),
  calculateBorderWidth: PropTypes.func,
  calculateBoxShadow: PropTypes.func,
  calculateCheckboxBackgroundColor: PropTypes.func,
  calculateBorderColor: PropTypes.func
};
var defaultProps = {
  disabled: false,
  error: false,
  height: 20,
  width: 20,
  surface: 'light',
  hovered: false,
  calculateBorderWidth: _calculateBorderWidth,
  calculateCheckboxBackgroundColor: _calculateCheckboxBackgroundColor,
  calculateBorderColor: _calculateBorderColor,
  calculateBoxShadow: _calculateBoxShadow
};
var CHECK_SIZE = 20;
var sharedHitAreaStyles = /*#__PURE__*/css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  left: ", ";\n  position: absolute;\n  transform: translate(-50%, -50%);\n  text-align: center;\n  top: 50%;\n"])), calculateRem(10));
var HitArea = /*#__PURE__*/styled.div.withConfig({
  displayName: "HitArea",
  componentId: "VDS__sc-hwcm7v-0"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  ", ";\n  cursor: ", ";\n  display: inline-block;\n  height: ", ";\n  width: ", ";\n"])), sharedHitAreaStyles, function (_ref5) {
  var disabled = _ref5.disabled;
  return disabled ? 'default' : 'pointer';
}, calculateRem(44), calculateRem(44));
var StyledWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledWrapper",
  componentId: "VDS__sc-hwcm7v-1"
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  box-sizing: border-box;\n  display: inline-block;\n  content: '';\n  height: ", ";\n  left: 50%;\n  position: relative;\n  top: 50%;\n  transform: translate(-50%, -50%);\n  width: ", ";\n"])), calculateRem(CHECK_SIZE), calculateRem(CHECK_SIZE));
var StyledInner = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledInner",
  componentId: "VDS__sc-hwcm7v-2"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  display: flex;\n  position: relative;\n  align-items: center;\n  justify-content: center;\n  top: 50%;\n  left: 50%;\n  box-sizing: border-box;\n  width: ", ";\n  height: ", ";\n  transform: translate(-50%, -50%);\n  background-color: ", ";\n  border-style: solid;\n  border-color: ", ";\n  border-radius: ", ";\n  border-width: ", ";\n  box-shadow: ", ";\n  ", "\n"])), calculateRem(CHECK_SIZE), calculateRem(CHECK_SIZE), function (_ref6) {
  var calculateCheckboxBackgroundColor = _ref6.calculateCheckboxBackgroundColor,
      props = _objectWithoutProperties(_ref6, _excluded);

  return calculateCheckboxBackgroundColor(props);
}, function (_ref7) {
  var calculateBorderColor = _ref7.calculateBorderColor,
      props = _objectWithoutProperties(_ref7, _excluded2);

  return calculateBorderColor(props);
}, function (_ref8) {
  var borderRadius = _ref8.borderRadius;
  return borderRadius;
}, function (_ref9) {
  var calculateBorderWidth = _ref9.calculateBorderWidth,
      props = _objectWithoutProperties(_ref9, _excluded3);

  return calculateBorderWidth(props);
}, function (_ref10) {
  var calculateBoxShadow = _ref10.calculateBoxShadow,
      props = _objectWithoutProperties(_ref10, _excluded4);

  return calculateBoxShadow(props);
}, function (_ref11) {
  var surface = _ref11.surface,
      disabled = _ref11.disabled;
  return !disabled && "\n    &:active {\n      border-color: ".concat(surface === 'dark' ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value, ";\n      box-shadow: 0 0 0 ").concat(calculateRem(FormControlsTokens.border.width.value), " ").concat(surface === 'dark' ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value, "\n    }\n  ");
});
var IconSVGWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "IconSVGWrapper",
  componentId: "VDS__sc-hwcm7v-3"
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  display: flex;\n  height: ", ";\n  width: ", ";\n  min-height: ", ";\n  min-width: ", ";\n  ", ";\n  &:active,\n  &:focus {\n    outline: none;\n  }\n  svg {\n    path {\n      fill: ", ";\n    }\n  }\n"])), function (_ref12) {
  var iconSize = _ref12.iconSize;
  return calculateRem(iconSize);
}, function (_ref13) {
  var iconSize = _ref13.iconSize;
  return calculateRem(iconSize);
}, function (_ref14) {
  var iconSize = _ref14.iconSize;
  return calculateRem(iconSize);
}, function (_ref15) {
  var iconSize = _ref15.iconSize;
  return calculateRem(iconSize);
}, function (_ref16) {
  var tabIndex = _ref16.tabIndex;
  return tabIndex < 0 && "\n    outline: none;\n    pointer-events: none;\n  ";
}, function (_ref17) {
  var iconColor = _ref17.iconColor;
  return iconColor;
});
/**
 * @ignore
 */

var CheckboxIcon = function CheckboxIcon(props) {
  var selected = props.selected || false;
  return /*#__PURE__*/React.createElement(StyledWrapper, null, /*#__PURE__*/React.createElement(HitArea, {
    error: props.error,
    disabled: props.disabled,
    surface: props.surface
  }), /*#__PURE__*/React.createElement(StyledInner, _extends({}, props, {
    hovered: props.hovered,
    error: props.error,
    disabled: props.disabled,
    surface: props.surface,
    className: "styledInner"
  }), selected && /*#__PURE__*/React.createElement(IconSVGWrapper, {
    "aria-hidden": true,
    iconSize: 12,
    tabIndex: -1,
    iconColor: props.surface === 'dark' ? ColorTokens.elements.primary.onlight.value : ColorTokens.elements.primary.ondark.value
  }, CheckmarkBoldIcon)));
};

CheckboxIcon.defaultProps = defaultProps;
CheckboxIcon.propTypes = propTypes;
export default CheckboxIcon;